import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { STATUS_COLUMNS, statusColumn } from "../../../constants/taskboardStatus";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

// Status pill with a one-click quick-select (product rule 2: status
// changes in one gesture, never buried in a modal). Shows the current
// status in its column color; clicking opens a small menu of the four
// statuses; picking one calls onChange and persists immediately in the
// parent. `readOnly` renders the pill without the menu; `title` lets the
// caller explain WHY it is read-only.
const StatusPill = ({ status, onChange, readOnly = false, size = "sm", title }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const col = statusColumn(status);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const pick = (id) => {
    setOpen(false);
    if (id !== col.id) onChange && onChange(id);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pad = size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-[12px]";

  if (readOnly) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full font-semibold ${pad}`}
        style={{ background: col.pillBg, color: col.pillColor }}
        title={title}
      >
        {col.label}
      </span>
    );
  }

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center gap-1 rounded-full font-semibold ${pad} hover:brightness-95`}
        style={{ background: col.pillBg, color: col.pillColor }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {col.label}
        <ChevronDown className="w-3 h-3" style={{ color: col.pillColor }} />
      </button>
      {open && !isMobile && (
        <div
          className="absolute left-0 top-full mt-1 z-30 w-40 bg-white border border-[#E4E9F0] rounded-lg shadow-lg py-1"
          role="listbox"
          onClick={(e) => e.stopPropagation()}
        >
          {STATUS_COLUMNS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={c.id === col.id}
              onClick={() => pick(c.id)}
              className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-[13px] hover:bg-[#F8FAFC]"
            >
              <span className="w-2 h-2 rounded-full flex-none" style={{ background: c.labelColor }} />
              <span style={{ color: c.labelColor }} className="font-medium">{c.label}</span>
              {c.id === col.id && <Check className="w-3.5 h-3.5 ml-auto text-[#1866CC]" />}
            </button>
          ))}
        </div>
      )}

      {/* Mobile: one-tap status via a bottom sheet (design 1m′) */}
      {open && isMobile && createPortal(
        <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setOpen(false)}>
          <div className="w-full bg-white rounded-t-2xl p-2 pb-6" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto w-10 h-1 rounded-full bg-[#E4E9F0] my-2" />
            <p className="px-3 py-2 text-[12px] font-semibold uppercase tracking-wide text-[#98A2B3]">Move to</p>
            {STATUS_COLUMNS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c.id)}
                className="flex items-center gap-3 w-full text-left px-4 py-3 text-[15px] hover:bg-[#F8FAFC] rounded-lg"
              >
                <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: c.labelColor }} />
                <span style={{ color: c.labelColor }} className="font-medium">{c.label}</span>
                {c.id === col.id && <Check className="w-4 h-4 ml-auto text-[#1866CC]" />}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
};

export default StatusPill;
